import React, { useRef, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import SettingsIcon from '@material-ui/icons/Settings'
import CloseIcon from '@material-ui/icons/Close'
import deepEqual from 'deep-equal'
import { Button, ClickAwayListener, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, List, ListItem, makeStyles, Snackbar, TableCell, TableRow } from '@material-ui/core'

import SettingsPopper from '../../../lib/components/SettingsPopper'
import RoleDetailsDialog from './RoleDetailsDialog'
import UserRoleDialog from './UserRoleDialog.js'
import { Alert } from '@material-ui/lab'
import actions from '../../../redux/actions'
import config from '../../../config'
import { DELETE_SECURITY_ROLE } from '../../../redux/data/types'
import { hasPermission } from '../../../lib/components/form/RestrictedComponent'

const useStyles = makeStyles(theme => ({
  warning: {
    color: '#721c24',
    '&:hover': {
      backgroundColor: '#f8d7da'
    },
    '&.disabled:hover': {
      backgroundColor: 'lightgray'
    },
  },
  userList: {
    maxHeight: '4rem',
    overflow: 'auto'
  },
  stickyBottom: {
    bottom: 0,
    position: 'sticky',
    width: '100%',
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: '1rem',
    paddingTop: '0.25rem',
    paddingBottom: '0.25rem',
    '& > *': {
      marginLeft: '1rem'
    }
  },
  dialogPaper: {
    overflowX: 'hidden'
  },
  tabs: {
    paddingBottom: '1rem',
  },
  generalTabFields: {
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      paddingBottom: '1rem'
    }
  },
  dialogTitle: {
    margin: 0,
    padding: '1rem',
  },
  closeButton: {
    position: 'absolute',
    right: '0.5rem',
    top: '0.5rem',
  }
}))

const DeleteModal = props => {
  const { name, onClose, onConfirm } = props
  const classes = useStyles()
  return <>
    <Dialog onClose={onClose} open={true}>
      <DialogTitle className={classes.dialogTitle}>
        Permanently Delete
      <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to permanently delete {name}?
      </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            onConfirm()
            onClose()
          }}
        >
          Delete Forever
      </Button>
      </DialogActions>
    </Dialog>
  </>
}

const SecurityRoleRow = props => {
  const { role, deleteRole, deletePending, deleteError } = props

  const [showDialog, setShowDialog] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [tempRole, setTempRole] = useState(role)
  const settingsRef = useRef(null)
  const classes = useStyles()
  const [showSnackbar, setShowSnackbar] = useState(deletePending || deleteError)
  let snackMessage = deletePending ? 'Deleting Role' : 'Role Deleted'
  if (deleteError) {
    snackMessage = 'Error: ' + deleteError
  }
  const handleCloseSnackbar = () => setShowSnackbar(false)

  useEffect(() => {
    if (deletePending || deleteError) {
      setShowSnackbar(true)
    }
  }, [deletePending, deleteError])

  const dialogs = {
    DETAILS: {
      key: 'DETAILS',
      text: 'View Details',
      disabled: !hasPermission('account_securityGroups.read'),
      dialog: <RoleDetailsDialog
        role={role}
        onClose={() => setShowDialog(false)}
      />
    },
    ASSIGN: {
      key: 'ASSIGN',
      text: 'Assign Users',
      disabled: !hasPermission('account.any_update'),
      dialog: <UserRoleDialog
        role={role}
        onClose={() => setShowDialog(false)}
      />
    },
    DELETE: {
      key: 'DELETE',
      text: 'Delete Role',
      dialog: <DeleteModal
        name={role.roleName}
        onConfirm={() => { deleteRole({ roleID: role.roleID }) }}
        onClose={() => setShowDialog(false)}
      />,
      disabled: parseInt(role.roleID) <= 100 || !hasPermission('account_securityGroups.delete'),
      className: classes.warning,
      tooltip: parseInt(role.roleID) <= 100 ? 'This role cannot be deleted' : ''
    },
  }

  const settingActions = Object.keys(dialogs).map(key => {
    const dialog = dialogs[key]
    return {
      onClick: () => setShowDialog(key),
      ...dialog
      // text: dialog.text,
      // disabled: dialog.disabled,
      // tooltip: dialog.tooltip,
      // className: dialog.className
    }
  })
  let dialog = dialogs[showDialog]?.dialog || null

  useEffect(() => {
    if (!deepEqual(role, tempRole)) {
      setTempRole(role)
    }
  }, [role])

  const handleSettingsPopper = open => setSettingsOpen(open)
  return <TableRow>
    <TableCell>
      {role.roleName}
    </TableCell>
    <TableCell>
      {role.roleDescription}
    </TableCell>
    <TableCell size="small" padding="none">
      <List className={classes.userList} dense disablePadding >
        {role.assignedUsers.map((user, i) => {
          return <ListItem key={'user list item' + i} disableGutters>
            <span>
              {user.firstName + ' ' + user.lastName}
            </span>
          </ListItem>
        })}
      </List>
    </TableCell>
    <TableCell padding="checkbox">
      <ClickAwayListener onClickAway={() => handleSettingsPopper(false)}>
        <IconButton onClick={() => handleSettingsPopper(true)}>
          <SettingsIcon ref={settingsRef} />
        </IconButton>
      </ClickAwayListener>
    </TableCell>
    <SettingsPopper
      open={settingsOpen}
      onClose={() => handleSettingsPopper(false)}
      parentRef={settingsRef}
      actions={settingActions}
    />
    {dialog}
    <Snackbar open={showSnackbar} autoHideDuration={12000} onClose={handleCloseSnackbar}>
      <Alert onClose={handleCloseSnackbar} severity={deleteError ? 'error' : 'warning'}>{snackMessage}</Alert>
    </Snackbar>
  </TableRow>
}

const mapStateToProps = (state, ownProps) => {
  const clientID = state.data.user.clientId
  let deletePending = state.request?.DELETE_SECURITY_ROLE && state.request.DELETE_SECURITY_ROLE.pending
  let deleteError = state.request?.DELETE_SECURITY_ROLE && state.request.DELETE_SECURITY_ROLE?.error?.message
  const deletedRoleID = state.request?.DELETE_SECURITY_ROLE?.postData?.roleID
  if (deletedRoleID !== ownProps.role.roleID ) {
    deletePending = undefined
    deleteError = undefined
  }
  return { clientID, deletePending, deleteError }
}

const mapDispatchToProps = dispatch => ({
  callDispatch: func => dispatch(func)
})

const mergeProps = (stateProps, _dispatchProps, ownProps) => {
  const dispatch = func => _dispatchProps.callDispatch(func)
  const clientID = stateProps.clientID
  const deleteRole = ({ roleID }) => {
    dispatch(actions.request.sendRequest({
      type: DELETE_SECURITY_ROLE,
      url: `${config.uris.crud}SecurityRoles/deleteSecurityRole`,
      postData: {
        clientID,
        roleID
      }
    }))
  }
  return { deleteRole, ...stateProps, ...ownProps }
}

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(SecurityRoleRow)